// QC specification attached to an Analysis definition (F-B1): the measured parameters with
// their acceptance criteria, plus a calculation formula. This is the GMP "method spec" the
// result-entry flow (F-C2) reads — judging is done live in the FE and authoritatively by the
// server's recompute against the SAME server-side spec.
//
// SPEC-063 (#4149) — THE SPLIT. What used to be one specification is now two things under two
// different regimes, and this slice holds both halves of the map every consumer already reads:
//
//   * `parameters` + `formula` — the PARAMETER SPECIFICATION. Versioned, e-signed by a reviewer
//     and a supervisor, approved in Change control. It is edited in its OWN LIMS menu
//     (LIMS › Analysis specifications) and therefore has its own endpoints at last:
//         GET   /lims/analysis-specs                      → {items: [...]} (the menu's list)
//         PATCH /lims/analysis-specs/<analysisDefinitionId> {spec, specChangeReason?} → {item}
//     Saving a CHANGE creates a draft version server-side; the EFFECTIVE spec that result entry
//     reads is untouched until the draft is approved. That is unchanged from before the move.
//   * `requiredConsumables` — the CONSUMABLE REQUIREMENTS. NOT versioned, NOT e-signed, no
//     Change-control entry. They stay on the analysis definition page and arrive on the
//     definition dump under its own nested `consumableSpec` key (they used to ride inside `spec`).
//
// Both halves still land in ONE map, keyed by definition id, because the run-time consumers
// (result entry, the task config's consumable picker) need the parameters and the requirements
// together and should not have to know that they are now saved through different doors.
//
// The definitions actions remain the source for `requiredConsumables` (and, for a team that has
// not opened the new menu, for the parameters too — a definition dump still carries `spec`).
// CRITICAL, unchanged: the server snapshots + recomputes results from ITS spec rows, so a spec
// that only lived FE-side would make every result recompute against an empty snapshot.

import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"
import { CALC_INPUT_SOURCES } from "./calcEngine"

// state shape:
//   {
//     byDefinition: { [analysisDefinitionId]: { parameters, formula, requiredConsumables } },
//     list: [ <menu row> ],          // LIMS › Analysis specifications, from GET /lims/analysis-specs
//     isLoading: false,
//   }
//
// A menu row is { analysisDefinitionId, name, parameters, formula, parameterCount,
//   effectiveVersionNumber|null, pendingVersionId|null, pendingVersionState|null,
//   pendingVersionNumber|null } — the EFFECTIVE spec plus the change-control status, so the list
// can say "in force" / "awaiting review" / "awaiting supervisor" without a request per analysis.
//
// A parameter is { id, name, unit, significantDigits, criterionType, min, max } and — since
// SPEC-016 (PRD-005 Area 2) — also { kind, inputs, formula, aggregate, templateId,
// individualTolerancePercent } for a CALCULATED parameter (a MEASURED parameter keeps these at
// their inert defaults). The spec-level `formula` is retained as a legacy reference.
const initialState = { byDefinition: {}, list: [], isLoading: false }

// A spec bound (min/max) is the raw value the analyst typed — a string or number, or null when
// unset. Coerce anything non-primitive (e.g. an object persisted by an earlier build) to null so
// it can never reach the result-judging logic or the editor's text inputs as "[object Object]".
function normalizeBound(v) {
  if (v === null || v === undefined || v === "") return null
  if (typeof v === "number" || typeof v === "string") return v
  return null
}

// SPEC-016 (PRD-005 Area 2) — a parameter is either MEASURED (direct entry, the legacy default)
// or CALCULATED (the analyst enters named raw inputs and the calculation engine derives + judges
// the value). `kind` absent ⇒ "measured" so every pre-SPEC-016 spec reads unchanged.
export const SPEC_PARAMETER_KIND = { MEASURED: "measured", CALCULATED: "calculated" }
// The allowed input sources now live with the engine (SPEC-023 added "parameter"); re-exported
// here so existing consumers of this module keep working unchanged.
export { CALC_INPUT_SOURCES }

// Normalise one declared input of a calculated parameter: { key, label, unit, source } — plus
// `parameterId` when the source is "parameter" (SPEC-023: the input binds to another parameter
// of the same spec by its STABLE id; names may change). A blank key / unknown source falls back
// to safe defaults so the engine + UI never see garbage; a missing/blank parameterId is kept as
// null (NOT silently downgraded to manual) so the reference validation flags it before save.
function normalizeInput(input) {
  const src = CALC_INPUT_SOURCES.includes(input && input.source) ? input.source : "manual"
  const normalized = {
    key: typeof (input && input.key) === "string" ? input.key.trim() : "",
    label: typeof (input && input.label) === "string" ? input.label : "",
    unit: typeof (input && input.unit) === "string" ? input.unit : "",
    source: src,
  }
  if (src === "parameter") {
    normalized.parameterId =
      typeof (input && input.parameterId) === "string" && input.parameterId.trim() !== ""
        ? input.parameterId
        : null
  }
  return normalized
}

// Normalise the new calculated-parameter fields (kind / inputs / per-parameter formula /
// aggregate / templateId / individualTolerancePercent), preserving the measured-parameter shape
// untouched. For a measured parameter the calculated-only fields are forced to their inert
// defaults, so toggling kind back and forth never leaves stale data on the record.
function normalizeParameter(p) {
  const param = p && typeof p === "object" ? p : {}
  const base = { ...param, min: normalizeBound(param.min), max: normalizeBound(param.max) }
  const kind = param.kind === SPEC_PARAMETER_KIND.CALCULATED ? SPEC_PARAMETER_KIND.CALCULATED : SPEC_PARAMETER_KIND.MEASURED
  if (kind !== SPEC_PARAMETER_KIND.CALCULATED) {
    // A MEASURED parameter has NO calculated state at all — including no per-parameter formula.
    // Keeping a stale formula on a measured parameter is the latent data-integrity hole behind
    // the "template seeded a formula but the inputs were wiped" bug (PRD-006 K21): this branch
    // wiped `inputs` to [] but PRESERVED `formula`, so a parameter that lost its calculated `kind`
    // ended up with a formula referencing inputs that no longer existed → "Unknown name(s): …".
    // Clearing the formula too keeps the wipe symmetric: a measured parameter has neither inputs
    // nor a formula, so the two can never diverge.
    return {
      ...base,
      kind: SPEC_PARAMETER_KIND.MEASURED,
      inputs: [],
      formula: "",
      aggregate: null,
      templateId: null,
      individualTolerancePercent: null,
    }
  }
  const inputs = (Array.isArray(param.inputs) ? param.inputs : []).map(normalizeInput).filter((i) => i.key !== "")
  const aggregate =
    param.aggregate && typeof param.aggregate.inputKey === "string" && param.aggregate.inputKey.trim() !== ""
      ? { inputKey: param.aggregate.inputKey.trim() }
      : null
  const indiv =
    param.individualTolerancePercent === null ||
    param.individualTolerancePercent === undefined ||
    param.individualTolerancePercent === ""
      ? null
      : param.individualTolerancePercent
  return {
    ...base,
    kind: SPEC_PARAMETER_KIND.CALCULATED,
    inputs,
    formula: typeof param.formula === "string" ? param.formula : "",
    aggregate,
    templateId: typeof param.templateId === "string" && param.templateId !== "" ? param.templateId : null,
    individualTolerancePercent: indiv,
  }
}

// Normalise a full parameter list. Exported so the definition editor can build the exact
// persisted shape it sends on the definition payload (the server stores it verbatim as JSONB).
export function normalizeParameters(parameters) {
  return (Array.isArray(parameters) ? parameters : []).map(normalizeParameter)
}

// F8 (SPEC-040) — the allowed consumable-requirement types (mirrors CONSUMABLE_TYPES in
// limsConsumables.js; kept here to avoid a cross-slice import for a tiny constant).
export const REQUIRED_CONSUMABLE_TYPES = ["reagent", "standard", "column", "glassware"]

// A requirement-field value counts as "blank" (unset) when null/undefined/empty-string, or a
// whitespace-only string. Quantity is advisory, so a blank field is "unspecified", not 0.
function isBlankReqValue(v) {
  if (v === null || v === undefined) return true
  return typeof v === "string" && v.trim() === ""
}

// Normalise the optional quantity object of a requirement → { min, max, unit } or null when every
// field is blank (quantity is advisory; an all-blank quantity is "unspecified").
function normalizeQuantity(quantity) {
  const q = quantity && typeof quantity === "object" ? quantity : null
  if (!q) return null
  if (isBlankReqValue(q.min) && isBlankReqValue(q.max) && isBlankReqValue(q.unit)) return null
  return {
    min: q.min === undefined ? null : q.min,
    max: q.max === undefined ? null : q.max,
    unit: typeof q.unit === "string" ? q.unit : "",
  }
}

// Normalise one consumable requirement on the analysis template (F8): a high-level demand the
// run's consumable selection must satisfy — NOT a serial/lot number. Shape:
//   { type, name, quantity: { min, max, unit } | null, standardClass | null, notes }
// An unknown/blank type folds to "reagent" (mirrors consumableType); standardClass is kept as-is
// or null. Garbage in never reaches the picker filter or the completeness gate.
function normalizeRequirement(req) {
  const r = req && typeof req === "object" ? req : {}
  return {
    type: REQUIRED_CONSUMABLE_TYPES.includes(r.type) ? r.type : "reagent",
    name: typeof r.name === "string" ? r.name : "",
    quantity: normalizeQuantity(r.quantity),
    standardClass: typeof r.standardClass === "string" && r.standardClass !== "" ? r.standardClass : null,
    notes: typeof r.notes === "string" ? r.notes : "",
  }
}

// Normalise a full consumable-requirements list. Exported so the definition editor builds the
// exact persisted shape it sends on the definition payload (`consumableSpec.requiredConsumables`).
export function normalizeRequiredConsumables(requirements) {
  return (Array.isArray(requirements) ? requirements : []).map(normalizeRequirement)
}

// Whether a definition dump carries a usable nested spec object (null/absent = no spec row).
function specOf(definition) {
  return definition && definition.spec && typeof definition.spec === "object" ? definition.spec : null
}

// SPEC-063: the consumable requirements arrive under their OWN nested key now. `spec` is read as a
// fallback for one reason only — a frontend build can meet a backend that has not been upgraded
// yet, and the requirements must not blink out of the picker in that window. The new key wins
// whenever it is present, which is always once the backend half is deployed.
function requiredConsumablesOf(definition) {
  const consumableSpec =
    definition && definition.consumableSpec && typeof definition.consumableSpec === "object"
      ? definition.consumableSpec
      : null
  if (consumableSpec) return normalizeRequiredConsumables(consumableSpec.requiredConsumables)
  const spec = specOf(definition)
  return normalizeRequiredConsumables(spec ? spec.requiredConsumables : [])
}

// One definition dump → its entry in the map, or null when it carries nothing worth keeping.
// A definition with requirements but NO parameter spec is a REAL state now (the specification is
// authored in its own menu, so a freshly created analysis has requirements long before it has
// limits) — such an entry must still exist, or the run-time consumable gate has nothing to read.
function entryOf(definition) {
  const spec = specOf(definition)
  const requiredConsumables = requiredConsumablesOf(definition)
  if (!spec && requiredConsumables.length === 0) return null
  return {
    parameters: normalizeParameters(spec ? spec.parameters : []),
    formula: (spec && spec.formula) || "",
    requiredConsumables,
  }
}

// Mirror one definition's nested keys into the map (or drop the entry when it has none).
function applyDefinition(byDefinition, definition) {
  if (!definition || !definition.id) return byDefinition
  const entry = entryOf(definition)
  const next = { ...byDefinition }
  if (entry) {
    next[definition.id] = entry
  } else if (next[definition.id]) {
    delete next[definition.id]
  }
  return next
}

// Merge the parameter spec from a /lims/analysis-specs row into the map WITHOUT disturbing the
// requirements already mirrored from the definition dump. The two halves have different sources
// now, so a merge that replaced the whole entry would blank the consumable gate every time the
// specifications menu loaded.
function applySpecRow(byDefinition, row) {
  if (!row || !row.analysisDefinitionId) return byDefinition
  const existing = byDefinition[row.analysisDefinitionId]
  return {
    ...byDefinition,
    [row.analysisDefinitionId]: {
      parameters: normalizeParameters(row.parameters),
      formula: row.formula || "",
      requiredConsumables: (existing && existing.requiredConsumables) || [],
    },
  }
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "analysisDefinitions/fetch": {
      const byDefinition = {}
      ;(action.res || []).forEach((item) => {
        if (!item || !item.id) return
        const entry = entryOf(item)
        if (entry) byDefinition[item.id] = entry
      })
      return { ...state, byDefinition }
    }
    case "analysisDefinitions/create":
    case "analysisDefinitions/edit":
      return { ...state, byDefinition: applyDefinition(state.byDefinition, action.res) }
    case "analysisDefinitions/remove": {
      if (!action.res || !state.byDefinition[action.res.id]) return state
      const byDefinition = { ...state.byDefinition }
      delete byDefinition[action.res.id]
      return { ...state, byDefinition, list: state.list.filter((r) => r.analysisDefinitionId !== action.res.id) }
    }
    case "limsSpecs/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsSpecs/fetch": {
      const rows = action.res || []
      let byDefinition = state.byDefinition
      rows.forEach((row) => {
        byDefinition = applySpecRow(byDefinition, row)
      })
      return { ...state, list: rows, byDefinition, isLoading: false }
    }
    case "limsSpecs/save": {
      const row = action.res
      if (!row || !row.analysisDefinitionId) return state
      const list = state.list.some((r) => r.analysisDefinitionId === row.analysisDefinitionId)
        ? state.list.map((r) => (r.analysisDefinitionId === row.analysisDefinitionId ? row : r))
        : [...state.list, row]
      // NB: the map is updated from the row's EFFECTIVE content, which a saved CHANGE deliberately
      // leaves untouched (the change is a draft until it is signed twice). Result entry must keep
      // judging against what is in force, so this is exactly right — do not merge the draft here.
      return { ...state, list, byDefinition: applySpecRow(state.byDefinition, row) }
    }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

// ---- thunks ----------------------------------------------------------------------------------

// The whole team's analyses with their effective parameter spec + change-control status — the
// LIMS › Analysis specifications list. Open to every LIMS user (the server gates only writing).
export const fetch = asyncAction("limsSpecs/fetch", () =>
  limsRequest("GET", "/lims/analysis-specs").then((res) => res.items)
)

// Save ONE analysis's parameter specification. Sends only the specification — never the rest of
// the definition — so nothing the menu does not show can be changed by accident. A CHANGE creates
// a draft version server-side and needs `reason` (audited); the first specification on an analysis
// that has none becomes its approved baseline and needs none.
//
// SPEC-070: `basedOnVersionId` is the pending version the editor CONTINUED from, when the author
// chose to. Omitted, the server records the version in force as the base (the default choice).
export const save = asyncAction("limsSpecs/save", (_dispatch, analysisDefinitionId, spec, reason, basedOnVersionId) =>
  limsRequest("PATCH", `/lims/analysis-specs/${analysisDefinitionId}`, {
    spec: {
      parameters: normalizeParameters(spec && spec.parameters),
      formula: (spec && spec.formula) || "",
      ...(basedOnVersionId ? { basedOnVersionId } : {}),
    },
    ...(reason && reason.trim() ? { specChangeReason: reason.trim() } : {}),
  }).then((res) => res.item)
)

// ---- selectors -------------------------------------------------------------------------------

// The spec for one analysis definition (or undefined if none stored yet).
export const selectSpec = (state, analysisDefinitionId) => state.limsSpecs.byDefinition[analysisDefinitionId]

// The Analysis-specifications list, by analysis name.
export const selectSpecList = (state) =>
  [...state.limsSpecs.list].sort((a, b) => (a.name || "").localeCompare(b.name || ""))

// One row of that list, or null.
export const selectSpecRow = (state, analysisDefinitionId) =>
  state.limsSpecs.list.find((r) => r.analysisDefinitionId === analysisDefinitionId) || null

// SPEC-067 (#4170) — is this definition's specification IN FORCE? One predicate, so the readiness
// column, the plan editor's per-analysis badge and anything else that asks cannot answer
// differently about the same analysis. "Has content" is NOT the question: a draft awaiting two
// signatures has content and is not in force, and result entry judges nothing against it.
//
// Reads the analysis-specifications list, so a caller must have fetched it (limsSpecs.fetch);
// without it every analysis reads as not in force rather than as wrongly in force.
export const selectHasEffectiveSpec = (state, analysisDefinitionId) => {
  const row = selectSpecRow(state, analysisDefinitionId)
  return !!(row && row.effectiveVersionNumber != null)
}

// The same question for a whole screen at once: the set of definition ids whose specification is in
// force, so a per-product pass is a membership test instead of a scan per analysis.
//
// A plain function over the rows, NOT a selector: a selector returning a fresh Set is never equal
// to its previous value, so it would re-render its component on every store change. Callers read
// `limsSpecs.list` and memoise this on it.
export const effectiveSpecIdsFrom = (rows) =>
  new Set(
    (rows || [])
      .filter((r) => r && r.analysisDefinitionId && r.effectiveVersionNumber != null)
      .map((r) => r.analysisDefinitionId)
  )

// Whether a spec parameter is calculated (engine-derived) vs measured (direct entry). `kind`
// absent reads as measured, so legacy parameters are correctly treated as measured.
export const isCalculatedParameter = (parameter) =>
  !!parameter && parameter.kind === SPEC_PARAMETER_KIND.CALCULATED
